import { useState } from "react"

import { BasePlaceCard } from "@/components/ui/PlaceCard"

// Mock place data for testing
const mockPlaces = [
  {
    id: '1',
    name: 'Sushi Saito',
    imageUrl: 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=600&h=600&fit=crop',
    category: 'Restaurant',
    rating: 4.8,
    priceLevel: 4,
    distance: '2.3km from hotel',
  },
  {
    id: '2',
    name: 'Tokyo Skytree',
    imageUrl: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&h=600&fit=crop',
    category: 'Attraction',
    rating: 4.5,
    priceLevel: 2,
    distance: '5.1km from hotel',
  },
  {
    id: '3',
    name: 'TeamLab Borderless',
    imageUrl: 'https://images.unsplash.com/photo-1518640467707-6811f4a6ab73?w=600&h=600&fit=crop',
    category: 'Museum',
    rating: 4.9,
    priceLevel: 3,
    distance: '3.7km from hotel',
  },
  {
    id: '4',
    name: 'Meiji Shrine',
    imageUrl: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=600&h=600&fit=crop',
    category: 'Temple',
    rating: 4.6,
    priceLevel: 1,
    distance: '1.2km from hotel',
  },
]

export function PlaceCardPreview() {
  const [selectedCard, setSelectedCard] = useState<string | null>(null)

  const handleTap = (placeId: string, placeName: string) => {
    setSelectedCard(placeId)
    console.log('Tapped:', placeName)
    // Reset after 2 seconds
    setTimeout(() => setSelectedCard(null), 2000)
  }

  const handleRemove = (placeName: string) => {
    console.log('Remove:', placeName)
    alert(`Remove: ${placeName}`)
  }

  const handleSchedule = (placeName: string) => {
    console.log('Schedule:', placeName)
    alert(`Schedule: ${placeName}`)
  }

  return (
    <div className="min-h-screen bg-magazine-cream p-8">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="font-serif text-4xl font-bold text-magazine-ink">
            BasePlaceCard Preview
          </h1>
          <p className="text-magazine-ash text-lg">
            Testing all variants, aspect ratios, and interactions
          </p>
          {selectedCard && (
            <div className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">
              ✓ Card tapped! (ID: {selectedCard})
            </div>
          )}
        </div>

        {/* Section: Compact Variant - Grid with Square (1:1) */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              Compact Variant - Square Grid (1:1)
            </h2>
            <p className="text-magazine-ash text-sm">
              Perfect for grid layouts. Hover to see scale effect.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {mockPlaces.map((place) => (
              <BasePlaceCard
                key={place.id}
                variant="compact"
                imageAspectRatio="1:1"
                place={place}
                onTap={() => handleTap(place.id, place.name)}
              />
            ))}
          </div>
        </section>

        {/* Section: Compact Variant - Horizontal Scroll with 6:5 */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              Compact Variant - Horizontal Scroll (6:5)
            </h2>
            <p className="text-magazine-ash text-sm">
              Saved candidates with red heart indicator. Scroll horizontally.
            </p>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {mockPlaces.map((place) => (
              <BasePlaceCard
                key={place.id}
                variant="compact"
                imageAspectRatio="6:5"
                place={place}
                showSaveIndicator={true}
                savedDate={new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)}
                savedFrom="search"
                onTap={() => handleTap(place.id, place.name)}
              />
            ))}
          </div>
        </section>

        {/* Section: Expanded Variant - 16:9 Candidates */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              Expanded Variant - Saved Candidates (16:9)
            </h2>
            <p className="text-magazine-ash text-sm">
              With action buttons (Remove / Schedule). Try clicking the buttons.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mockPlaces.slice(0, 2).map((place) => (
              <BasePlaceCard
                key={place.id}
                variant="expanded"
                imageAspectRatio="16:9"
                place={place}
                showSaveIndicator={true}
                savedDate={new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)}
                savedFrom="map"
                userNote="Highly recommended by locals. Make sure to book ahead!"
                onTap={() => handleTap(place.id, place.name)}
                onRemove={() => handleRemove(place.name)}
                onSchedule={() => handleSchedule(place.name)}
              />
            ))}
          </div>
        </section>

        {/* Section: Expanded Variant - 16:9 Search Results */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              Expanded Variant - Search Results (16:9)
            </h2>
            <p className="text-magazine-ash text-sm">
              Search result cards with enhanced metadata (Category • Price • Status | Neighborhood). No action buttons.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mockPlaces.map((place) => (
              <BasePlaceCard
                key={place.id}
                variant="expanded"
                imageAspectRatio="16:9"
                place={{
                  ...place,
                  // Enhanced metadata for search results
                  category: `${place.category} • ${place.priceLevel === 4 ? 'Luxury' : place.priceLevel === 3 ? 'Upscale' : place.priceLevel === 2 ? 'Moderate' : 'Budget-friendly'} • Open now`,
                  distance: 'Shibuya', // Neighborhood instead of distance
                }}
                onTap={() => handleTap(place.id, place.name)}
              />
            ))}
          </div>
        </section>

        {/* Section: Comparison - All Aspect Ratios */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              All Aspect Ratios Comparison
            </h2>
            <p className="text-magazine-ash text-sm">
              Same place, different aspect ratios: 1:1, 6:5, 16:9, 4:3
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">1:1 Square</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="1:1"
                place={mockPlaces[0]}
                onTap={() => handleTap(mockPlaces[0].id, mockPlaces[0].name)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">6:5 Portrait</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="6:5"
                place={mockPlaces[0]}
                showSaveIndicator={true}
                savedDate={new Date()}
                onTap={() => handleTap(mockPlaces[0].id, mockPlaces[0].name)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">16:9 Wide</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="16:9"
                place={mockPlaces[0]}
                onTap={() => handleTap(mockPlaces[0].id, mockPlaces[0].name)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">4:3 Classic</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="4:3"
                place={mockPlaces[0]}
                onTap={() => handleTap(mockPlaces[0].id, mockPlaces[0].name)}
              />
            </div>
          </div>
        </section>

        {/* Section: Saved vs Unsaved */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              Saved vs Unsaved States
            </h2>
            <p className="text-magazine-ash text-sm">
              Red heart appears only on saved items (used sparingly, monochromatic otherwise)
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">Unsaved</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="1:1"
                place={mockPlaces[0]}
                showSaveIndicator={false}
                onTap={() => handleTap(mockPlaces[0].id, mockPlaces[0].name)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">Saved ❤️</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="1:1"
                place={mockPlaces[0]}
                showSaveIndicator={true}
                savedDate={new Date()}
                onTap={() => handleTap(mockPlaces[0].id, mockPlaces[0].name)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">Unsaved</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="1:1"
                place={mockPlaces[1]}
                showSaveIndicator={false}
                onTap={() => handleTap(mockPlaces[1].id, mockPlaces[1].name)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-center text-xs text-magazine-ash font-medium">Saved ❤️</p>
              <BasePlaceCard
                variant="compact"
                imageAspectRatio="1:1"
                place={mockPlaces[1]}
                showSaveIndicator={true}
                savedDate={new Date()}
                onTap={() => handleTap(mockPlaces[1].id, mockPlaces[1].name)}
              />
            </div>
          </div>
        </section>

        {/* Section: Timeline Variant */}
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-magazine-ink mb-2">
              Timeline Variant (Future)
            </h2>
            <p className="text-magazine-ash text-sm">
              Full-width cards for schedule timeline view. No save indicator.
            </p>
          </div>
          <div className="space-y-4 max-w-2xl">
            {mockPlaces.slice(0, 2).map((place) => (
              <BasePlaceCard
                key={place.id}
                variant="timeline"
                imageAspectRatio="16:9"
                place={place}
                showSaveIndicator={false}
                onTap={() => handleTap(place.id, place.name)}
              />
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="text-center pt-8 pb-4 border-t border-magazine-pearl">
          <p className="text-magazine-ash text-sm">
            Open browser console to see interaction logs
          </p>
        </div>
      </div>
    </div>
  )
}
